import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListSubheader,
  Alert,
  CircularProgress,
  Box,
  Stack,
  Avatar,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Delete as DeleteIcon, VisibilityOff as VisibilityOffIcon, PhotoCamera as PhotoCameraIcon } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { fr } from 'date-fns/locale';
import apiService from '../services/api';
import { useProviderInfo } from '../hooks/useProviderInfo';
import { invalidateFoundObjectStats } from '../lib/messagingQueryCache';
import {
  FoundObject,
  FoundObjectStatus,
  CreateFoundObjectRequest,
  UpdateFoundObjectRequest,
  Category,
  CategoryAttribute,
  ProviderEventRow,
} from '../types/foundObject';
import ImageUpload from './ImageUpload';
import ConditionalAttributeField from './ConditionalAttributeField';
import LocationFields from './LocationFields';
import CategoryIcon from './CategoryIcon';
import type { ProviderStorageBoxRow } from '../types/storageBox';
import { getAnnouncementImageUrl } from '../utils/imageUrl';
import { toYmdForDateInput } from '../utils/toYmdForDateInput';
import { SECRET_OBJECT_ATTRIBUTE_KEY, validateSecretCluesValue } from '../utils/secretObjectAttributes';
import { getSecretCluesPlaceholder } from '../utils/secretCluesPlaceholder';
import FormattedReferenceCode from './FormattedReferenceCode';
import { buildProviderFoundObjectTitle } from '../utils/announcementAutoTitle';
import { isSplitPlaceStorageMode } from '../utils/placeStorageMode';
import { muiDialogTransitionProps, releaseFocus } from '../utils/dialogA11y';

const FOUND_OBJECT_IMAGES_REQUIRED_MSG = "Ajoutez au moins une photo de l'objet.";

function hasFoundObjectImages(images?: string[] | null): boolean {
  return (images ?? []).some((img) => String(img).trim() !== '');
}

type PostRegisterSummary = {
  reference_code: string;
  title: string;
  boxName: string;
};

function toDateInputString(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Jour calendaire local (évite `Date.now() - 24h` et les décalages UTC sur `YYYY-MM-DD`). */
function calendarDayStringDaysAgo(daysAgo: number): string {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  return toDateInputString(d);
}

function parseYmdToLocalDate(ymd: string): Date | null {
  const s = String(ymd || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const [y, m, d] = s.split('-').map((x) => parseInt(x, 10));
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  return new Date(y, m - 1, d);
}

function normalizeText(value: unknown): string {
  if (value == null) return '';
  const text = String(value).trim();
  return text;
}

function resolveFoundObjectType(fo: FoundObject): string {
  return normalizeText(fo.objectType ?? fo.object_type);
}

function resolveFoundObjectAttributes(fo: FoundObject): Record<string, unknown> {
  const raw = fo.objectAttributes ?? fo.object_attributes;
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return { ...(raw as Record<string, unknown>) };
  }
  return {};
}

function resolveCategoryForFoundObject(
  fo: FoundObject,
  categories: Category[] | undefined
): Category | undefined {
  if (!categories?.length) return undefined;

  const objectType = resolveFoundObjectType(fo);
  if (objectType) {
    const byName = categories.find((cat) => cat.name === objectType);
    if (byName) return byName;
  }

  const categoryId =
    fo.storage_box?.category_id ??
    fo.storage_box?.category?.id ??
    null;
  if (categoryId != null) {
    return categories.find((cat) => cat.id === categoryId);
  }

  return undefined;
}

interface FoundObjectDialogProps {
  open: boolean;
  onClose: () => void;
  object?: FoundObject | null;
}

const FoundObjectDialog: React.FC<FoundObjectDialogProps> = ({ open, onClose, object }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [formData, setFormData] = useState<
    CreateFoundObjectRequest & {
      categoryId?: number | null;
      objectAttributes?: Record<string, any>;
      locationData?: Record<string, any>;
      provider_event_id?: number | null;
      status: FoundObjectStatus;
    }
  >({
    title: '',
    objectType: '',
    foundAt: calendarDayStringDaysAgo(0),
    location: '',
    status: 'found',
    images: [] as string[],
    categoryId: null,
    objectAttributes: {},
    locationData: {},
    provider_event_id: null,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  /** Création : choix de boîte à l’enregistrement (modale). */
  const [storagePickOpen, setStoragePickOpen] = useState(false);
  const [storagePickSelection, setStoragePickSelection] = useState('');
  /** Après création : consigne étiquette + référence. */
  const [postRegisterOpen, setPostRegisterOpen] = useState(false);
  const [postRegisterData, setPostRegisterData] = useState<PostRegisterSummary | null>(null);
  const [imageUploading, setImageUploading] = useState(false);
  const quickImageInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  // Récupérer les catégories (rafraîchi à l’ouverture pour placeholder / attributs à jour)
  const { data: categoriesData, isLoading: categoriesLoading, refetch: refetchCategories } = useQuery({
    queryKey: ['categories'],
    queryFn: () => apiService.getCategories(),
    select: (response) => response.data.categories || [],
    enabled: open,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  useEffect(() => {
    if (open) {
      void refetchCategories();
    }
  }, [open, refetchCategories]);

  const { data: providerInfo } = useProviderInfo();
  const isSplitMode = isSplitPlaceStorageMode(providerInfo);

  const { data: providerItemsList = [] } = useQuery({
    queryKey: ['provider-items', providerInfo?.id],
    queryFn: async () => {
      const res = await apiService.getProviderItems(providerInfo!.id);
      return res.data.provider_items || [];
    },
    enabled: open && !!providerInfo?.id,
  });

  const { data: storageBoxesList = [] } = useQuery({
    queryKey: ['provider-storage-boxes', providerInfo?.id],
    queryFn: async () => {
      const res = await apiService.getStorageBoxesByProvider(providerInfo!.id);
      return res.data.storage_boxes || [];
    },
    enabled: open && !!providerInfo?.id,
  });

  const activeStorageBoxes = useMemo(
    () => (storageBoxesList as ProviderStorageBoxRow[]).filter((b) => b.is_active !== false),
    [storageBoxesList]
  );

  const groupedStorageByPlace = useMemo(() => {
    return activeStorageBoxes.reduce<Record<string, ProviderStorageBoxRow[]>>((acc, box) => {
      const placeLabel = box.storage_scope === 'central' || !box.provider_place
        ? 'Stockage central'
        : (box.provider_place?.display_name || box.provider_place?.name || 'Lieu').trim() || 'Lieu';
      if (!acc[placeLabel]) {
        acc[placeLabel] = [];
      }
      acc[placeLabel].push(box);
      return acc;
    }, {});
  }, [activeStorageBoxes]);

  const isStorageBoxMarkedFull = (box: ProviderStorageBoxRow): boolean => {
    const label = `${box.name ?? ''} ${box.description ?? ''}`.toLowerCase();
    return label.includes('pleine') || label.includes('full');
  };

  const getStorageBoxOrderScore = (box: ProviderStorageBoxRow): number => {
    const suffixMatch = String(box.name || '').match(/-(\d+)\s*$/);
    if (suffixMatch?.[1]) {
      const parsed = Number.parseInt(suffixMatch[1], 10);
      if (Number.isFinite(parsed)) return parsed;
    }
    return box.sort_order ?? 0;
  };

  // Récupérer le template du partenaire pour les champs de lieu
  const { data: providerTemplateData } = useQuery({
    queryKey: ['providerTemplate', providerInfo?.type],
    queryFn: () => apiService.getProviderTemplate(providerInfo?.type || ''),
    enabled: open && !!providerInfo?.type,
  });

  const providerTemplate = providerTemplateData?.data || null;

  const { data: providerEventsRes } = useQuery({
    queryKey: ['provider-events'],
    queryFn: () => apiService.getProviderEvents(),
    enabled: open && !!providerInfo?.provider_type?.annual_event,
  });
  const providerEvents: ProviderEventRow[] = providerEventsRes?.data?.provider_events ?? [];

  const editObjectId = open && object?.id ? object.id : undefined;

  const { data: fullEditObject } = useQuery({
    queryKey: ['found-object', editObjectId],
    queryFn: () => apiService.getFoundObject(editObjectId!).then((r) => r.data.data),
    enabled: !!editObjectId,
  });

  const editSourceObject = fullEditObject ?? object ?? null;

  // Récupérer les détails de la catégorie sélectionnée
  const { data: categoryData } = useQuery({
    queryKey: ['category', formData.categoryId],
    queryFn: () => apiService.getCategory(formData.categoryId!),
    select: (response) => {
      const payload = response.data as unknown as Category & { category?: Category };
      return payload.category ?? payload;
    },
    enabled: open && !!formData.categoryId,
  });

  useEffect(() => {
    if (!categoryData?.id || !formData.categoryId) return;
    if (Number(categoryData.id) !== Number(formData.categoryId)) return;
    setSelectedCategory(categoryData);
  }, [categoryData, formData.categoryId]);

  /** Liste + détail API : évite une catégorie « stale » sans secret_clues_placeholder. */
  const resolvedCategory = useMemo((): Category | null => {
    const id = Number(formData.categoryId);
    if (!id) {
      return selectedCategory;
    }
    if (categoryData && Number(categoryData.id) === id) {
      return categoryData;
    }
    const fromList = categoriesData?.find((c) => c.id === id);
    if (fromList) {
      return fromList;
    }
    if (selectedCategory && Number(selectedCategory.id) === id) {
      return selectedCategory;
    }
    return null;
  }, [formData.categoryId, categoryData, categoriesData, selectedCategory]);

  const secretCluesHint = useMemo(
    () => getSecretCluesPlaceholder(resolvedCategory),
    [resolvedCategory]
  );

  useEffect(() => {
    if (!open) return;

    if (editSourceObject) {
      const matchingCategory = resolveCategoryForFoundObject(
        editSourceObject,
        categoriesData as Category[] | undefined
      );
      const objectType = resolveFoundObjectType(editSourceObject) || matchingCategory?.name || '';

      const objectAttributes = resolveFoundObjectAttributes(editSourceObject);
      const locationData: Record<string, any> = {};

      const sbRaw =
        editSourceObject.storage_box_id ??
        editSourceObject.storage_box?.id ??
        objectAttributes.storage_box_id;
      if (sbRaw != null && sbRaw !== '') {
        locationData.storage_box_id = typeof sbRaw === 'string' ? Number(sbRaw) : sbRaw;
      }

      if (objectAttributes.provider_item_id != null && objectAttributes.provider_item_id !== '') {
        const raw = objectAttributes.provider_item_id;
        locationData.provider_item_id = typeof raw === 'string' ? Number(raw) : raw;
      }

      const foundPlaceRaw =
        editSourceObject.found_provider_item_id ??
        editSourceObject.found_provider_item?.id ??
        objectAttributes.found_provider_item_id;
      if (foundPlaceRaw != null && foundPlaceRaw !== '') {
        locationData.found_provider_item_id =
          typeof foundPlaceRaw === 'string' ? Number(foundPlaceRaw) : foundPlaceRaw;
      }

      if (providerTemplate?.type === 'hotel' && objectAttributes.room) {
        locationData.room = objectAttributes.room;
      }

      if (providerTemplate?.fields) {
        Object.keys(providerTemplate.fields).forEach((fieldName) => {
          if (objectAttributes[fieldName]) {
            locationData[fieldName] = objectAttributes[fieldName];
          }
        });
      }

      const st = editSourceObject.status;
      const allowed: FoundObjectStatus[] = ['found', 'to_ship', 'in_transit', 'claimed', 'disposed'];
      setFormData({
        title: editSourceObject.title,
        objectType,
        foundAt:
          toYmdForDateInput(editSourceObject.foundAt ?? editSourceObject.found_at) ??
          calendarDayStringDaysAgo(0),
        location: editSourceObject.location,
        status: allowed.includes(st as FoundObjectStatus) ? (st as FoundObjectStatus) : 'found',
        images: editSourceObject.images || [],
        categoryId: matchingCategory?.id ?? null,
        objectAttributes,
        locationData,
        provider_event_id:
          editSourceObject.provider_event_id ?? editSourceObject.provider_event?.id ?? null,
      });

      if (matchingCategory) {
        setSelectedCategory(matchingCategory);
      }
    } else {
      setFormData({
        title: '',
        objectType: '',
        foundAt: calendarDayStringDaysAgo(0),
        location: '',
        status: 'found',
        images: [] as string[],
        categoryId: null,
        objectAttributes: {},
        locationData: {},
        provider_event_id: null,
      });
      setSelectedCategory(null);
    }
    setErrors({});
  }, [editSourceObject, open, categoriesData, providerTemplate]);

  useEffect(() => {
    if (!open) {
      setStoragePickOpen(false);
      setStoragePickSelection('');
      setPostRegisterOpen(false);
      setPostRegisterData(null);
    }
  }, [open]);

  const createMutation = useMutation({
    mutationFn: (data: CreateFoundObjectRequest) => apiService.createFoundObject(data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['found-objects'] });
      void invalidateFoundObjectStats(queryClient);
      setStoragePickOpen(false);
      const fo = res.data?.found_object;
      if (fo) {
        setPostRegisterData({
          reference_code: fo.reference_code || '—',
          title: fo.title || '',
          boxName: fo.storage_box?.name || '—',
        });
        setPostRegisterOpen(true);
      } else {
        releaseFocus();
        onClose();
      }
    },
    onError: (error: any) => {
      console.error('Erreur lors de la création:', error);
      const api = error.response?.data;
      if (api?.errors && typeof api.errors === 'object') {
        setErrors(api.errors);
      } else if (api?.error) {
        setErrors({ general: String(api.error) });
      } else {
        setErrors({ general: 'Erreur lors de la création. Vérifiez les champs et réessayez.' });
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateFoundObjectRequest }) =>
      apiService.updateFoundObject(id, data),
    onSuccess: (response, variables) => {
      const fo = response.data?.found_object;
      if (fo) {
        queryClient.setQueryData(['found-object', variables.id], fo);
      } else {
        void queryClient.invalidateQueries({ queryKey: ['found-object', variables.id] });
      }
      void queryClient.invalidateQueries({ queryKey: ['found-objects'] });
      void invalidateFoundObjectStats(queryClient);
      releaseFocus();
      onClose();
    },
    onError: (error: any) => {
      console.error('Erreur lors de la mise à jour:', error);
      const api = error.response?.data;
      if (api?.errors && typeof api.errors === 'object') {
        setErrors(api.errors);
      } else if (api?.error) {
        setErrors({ general: String(api.error) });
      } else {
        setErrors({ general: 'Erreur lors de la mise à jour.' });
      }
    },
  });

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    if (field === 'images' && hasFoundObjectImages(value)) {
      setErrors((prev) => {
        if (!prev.images) return prev;
        const { images: _removed, ...rest } = prev;
        return rest;
      });
    }
    
    // Réinitialiser les attributs d'objet si la catégorie change
    if (field === 'categoryId') {
      setFormData(prev => ({
        ...prev,
        objectAttributes: {},
      }));
      setSelectedCategory(null);
    }
  };

  const handleLocationDataChange = (key: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      locationData: {
        ...(prev.locationData || {}),
        [key]: value,
      },
    }));
  };

  const handleCategoryChange = (rawCategoryId: number | string) => {
    const categoryId = Number(rawCategoryId);
    if (!Number.isFinite(categoryId)) {
      return;
    }
    const category = categoriesData?.find((c) => c.id === categoryId) ?? null;
    setSelectedCategory(category);
    setFormData((prev) => ({
      ...prev,
      categoryId,
      objectAttributes: {},
    }));
  };

  const handleAttributeChange = (attributeName: string, value: any) => {
    setFormData(prev => {
      const nextAttributes: Record<string, any> = {
        ...prev.objectAttributes,
        [attributeName]: value,
      };

      if (attributeName === 'card_type' && resolvedCategory?.attributes?.issuer) {
        delete nextAttributes.issuer;
      }

      return {
        ...prev,
        objectAttributes: nextAttributes,
      };
    });
    if (attributeName === SECRET_OBJECT_ATTRIBUTE_KEY) {
      setErrors((prev) => {
        if (!prev[SECRET_OBJECT_ATTRIBUTE_KEY]) {
          return prev;
        }
        const next = { ...prev };
        delete next[SECRET_OBJECT_ATTRIBUTE_KEY];
        return next;
      });
    }
  };

  // Convertir l'objet attributes en tableau d'objets CategoryAttribute
  const convertAttributesToArray = (attributesObj: any): CategoryAttribute[] => {
    if (!attributesObj || typeof attributesObj !== 'object') {
      return [];
    }
    
    return Object.entries(attributesObj)
      .filter(([, attr]: [string, any]) => !attr?.disabled)
      .map(([name, attr]: [string, any]) => ({
      name,
      type: attr.type,
      label: attr.label,
      required: attr.required || false,
      placeholder: typeof attr.placeholder === 'string' ? attr.placeholder : undefined,
      secret_detail: !!attr.secret_detail,
      use_as_filter: !!attr.use_as_filter,
      disabled: !!attr.disabled,
      options: attr.options,
      depends_on: typeof attr.depends_on === 'string' ? attr.depends_on : undefined,
      conditional_labels:
        attr.conditional_labels && typeof attr.conditional_labels === 'object'
          ? attr.conditional_labels
          : undefined,
      conditional: attr.conditional,
      colorOptions: attr.options && Array.isArray(attr.options) 
        ? attr.options.map((opt: any) => ({
            name: opt.label || opt.name || opt,
            value: opt.value || opt.label || opt.name || opt,
            hex: opt.hex || null
          }))
        : undefined
    }));
  };

  const renderAttributeField = (attribute: CategoryAttribute) => {
    const value = formData.objectAttributes?.[attribute.name] ?? '';

    return (
      <ConditionalAttributeField
        key={attribute.name}
        attributeKey={attribute.name}
        attribute={attribute}
        value={value}
        dependentValues={formData.objectAttributes || {}}
        onChange={(newValue) => handleAttributeChange(attribute.name, newValue)}
      />
    );
  };

  const mergeLocationDataWithBox = (forcedStorageBoxId?: number): Record<string, any> => {
    const base = { ...(formData.locationData || {}) };
    if (forcedStorageBoxId != null && Number.isFinite(forcedStorageBoxId)) {
      const box = (storageBoxesList as ProviderStorageBoxRow[]).find((b) => b.id === forcedStorageBoxId);
      base.storage_box_id = forcedStorageBoxId;
      if (!isSplitMode && box?.provider_item_id != null) {
        base.provider_item_id = box.provider_item_id;
      }
      if (providerTemplate?.type === 'hotel' && box?.name) {
        base.room = box.name;
      }
    }
    return base;
  };

  const resolveLocationText = (loc: Record<string, any>): string => {
    let locationText = formData.location;
    const foundPlaceId = loc.found_provider_item_id ?? loc.provider_item_id;
    const boxId = loc.storage_box_id;

    if (isSplitMode) {
      const parts: string[] = [];
      if (foundPlaceId != null && foundPlaceId !== '' && providerItemsList.length > 0) {
        const item = providerItemsList.find((i) => i.id === Number(foundPlaceId));
        if (item) parts.push(item.display_name || item.name);
      }
      if (boxId != null && boxId !== '' && storageBoxesList.length > 0) {
        const box = (storageBoxesList as ProviderStorageBoxRow[]).find((b) => b.id === Number(boxId));
        if (box) parts.push(box.name);
      }
      if (parts.length > 0) return parts.join(' — ');
    }

    if (boxId != null && boxId !== '' && storageBoxesList.length > 0) {
      const box = (storageBoxesList as ProviderStorageBoxRow[]).find((b) => b.id === Number(boxId));
      if (box) {
        const pn = box.provider_place?.display_name || box.provider_place?.name || 'Lieu';
        locationText = `${pn} — ${box.name}`;
      }
    } else if (providerTemplate && loc && Object.keys(loc).length > 0) {
      if (providerTemplate.type === 'hotel' && loc.room) {
        locationText = `Chambre ${loc.room}`;
      } else {
        const entries = Object.entries(loc).filter(
          ([k, value]) =>
            value !== '' &&
            value !== null &&
            k !== 'provider_item_id' &&
            k !== 'found_provider_item_id' &&
            k !== 'storage_box_id'
        );
        if (entries.length > 0) {
          locationText = entries.map(([key, value]) => `${key}: ${value}`).join(', ');
        }
      }
    } else {
      const pid = loc.provider_item_id;
      if (pid != null && pid !== '' && providerItemsList.length > 0) {
        const item = providerItemsList.find((i) => i.id === Number(pid));
        if (item) {
          locationText = item.display_name || item.name;
        }
      }
    }
    return locationText;
  };

  const buildSubmitDataFromLocation = (
    loc: Record<string, any>,
    isUpdate: boolean
  ): CreateFoundObjectRequest & {
    objectAttributes: Record<string, unknown>;
    provider_event_id?: number;
    status?: FoundObjectStatus;
    storage_box_id?: number;
  } => {
    const locationText = resolveLocationText(loc);
    const locationPayload = { ...loc };
    delete locationPayload.storage_box_id;
    if (locationPayload.provider_item_id != null && locationPayload.provider_item_id !== '') {
      locationPayload.provider_item_id = String(locationPayload.provider_item_id);
    }
    if (locationPayload.found_provider_item_id != null && locationPayload.found_provider_item_id !== '') {
      locationPayload.found_provider_item_id = String(locationPayload.found_provider_item_id);
    }

    const storageBoxIdRaw = loc.storage_box_id;
    const storageBoxIdNum =
      storageBoxIdRaw != null && storageBoxIdRaw !== '' ? Number(storageBoxIdRaw) : undefined;
    const foundPlaceRaw = loc.found_provider_item_id ?? loc.provider_item_id;
    const foundPlaceIdNum =
      foundPlaceRaw != null && foundPlaceRaw !== '' ? Number(foundPlaceRaw) : undefined;

    return {
      title: formData.title,
      objectType: resolvedCategory?.name || formData.objectType,
      foundAt: formData.foundAt,
      location: locationText,
      status: isUpdate ? formData.status : 'found',
      images: formData.images || [],
      objectAttributes: {
        ...formData.objectAttributes,
        ...locationPayload,
      },
      provider_event_id:
        providerInfo?.provider_type?.annual_event && formData.provider_event_id
          ? formData.provider_event_id
          : undefined,
      ...(storageBoxIdNum !== undefined && !Number.isNaN(storageBoxIdNum)
        ? { storage_box_id: storageBoxIdNum }
        : {}),
      ...(foundPlaceIdNum !== undefined && !Number.isNaN(foundPlaceIdNum)
        ? { found_provider_item_id: foundPlaceIdNum }
        : {}),
    };
  };

  const handleConfirmStorageAndCreate = () => {
    setErrors({});
    const secretCluesError = validateSecretCluesValue(formData.objectAttributes);
    if (secretCluesError) {
      setErrors({ [SECRET_OBJECT_ATTRIBUTE_KEY]: secretCluesError });
      return;
    }
    if (!hasFoundObjectImages(formData.images)) {
      setErrors({ images: FOUND_OBJECT_IMAGES_REQUIRED_MSG });
      setStoragePickOpen(false);
      return;
    }
    const boxId = parseInt(storagePickSelection, 10);
    if (!Number.isFinite(boxId) || boxId <= 0) {
      setErrors({ storage: 'Choisissez une boîte de stockage.' });
      return;
    }
    const loc = mergeLocationDataWithBox(boxId);
    const submitData = buildSubmitDataFromLocation(loc, false);
    createMutation.mutate(submitData);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const secretCluesError = validateSecretCluesValue(formData.objectAttributes);
    if (secretCluesError) {
      setErrors({ [SECRET_OBJECT_ATTRIBUTE_KEY]: secretCluesError });
      return;
    }

    if (!hasFoundObjectImages(formData.images)) {
      setErrors({ images: FOUND_OBJECT_IMAGES_REQUIRED_MSG });
      return;
    }

    if (!object) {
      if (!formData.categoryId) {
        setErrors({ categoryId: 'Choisissez une catégorie.' });
        return;
      }
      const hasBoxes = storageBoxesList.length > 0;
      const legacyPid = formData.locationData?.provider_item_id;
      if (hasBoxes) {
        if (activeStorageBoxes.length === 0) {
          setErrors({
            storage:
              'Aucune boîte de stockage active. Créez ou activez une boîte dans le menu Stockage.',
          });
          return;
        }
        const catId = resolvedCategory?.id;
        const preferredByCategory = activeStorageBoxes
          .filter((b) => b.category_id === catId)
          .sort((a, b) => getStorageBoxOrderScore(b) - getStorageBoxOrderScore(a))[0];
        const preferred = preferredByCategory ?? activeStorageBoxes[0];
        setStoragePickSelection(preferred ? String(preferred.id) : '');
        releaseFocus();
        setStoragePickOpen(true);
        return;
      }
      if (!hasBoxes && (legacyPid == null || legacyPid === '')) {
        setErrors({
          storage: 'Aucune boîte configurée : créez-en dans Stockage, ou renseignez le lieu.',
        });
        return;
      }
    }

    const loc = object ? { ...(formData.locationData || {}) } : mergeLocationDataWithBox();
    const submitData = buildSubmitDataFromLocation(loc, !!object);

    if (object) {
      updateMutation.mutate({
        id: object.id,
        data: { ...submitData, status: formData.status },
      });
    } else {
      createMutation.mutate(submitData);
    }
  };

  const handlePostRegisterDone = () => {
    releaseFocus();
    setPostRegisterOpen(false);
    setPostRegisterData(null);
    onClose();
  };

  const handleDialogClose = () => {
    releaseFocus();
    onClose();
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  const availableCategoryBoxes = useMemo(() => {
    const categoryId = resolvedCategory?.id;
    if (!categoryId) return [];
    return (activeStorageBoxes as ProviderStorageBoxRow[])
      .filter((b) => b.category_id === categoryId && !isStorageBoxMarkedFull(b))
      .sort((a, b) => getStorageBoxOrderScore(b) - getStorageBoxOrderScore(a));
  }, [activeStorageBoxes, resolvedCategory?.id]);

  const fullCategoryBoxes = useMemo(() => {
    const categoryId = resolvedCategory?.id;
    if (!categoryId) return [];
    return (activeStorageBoxes as ProviderStorageBoxRow[])
      .filter((b) => b.category_id === categoryId && isStorageBoxMarkedFull(b))
      .sort((a, b) => getStorageBoxOrderScore(b) - getStorageBoxOrderScore(a));
  }, [activeStorageBoxes, resolvedCategory?.id]);

  const nextCategoryBoxLabel = useMemo(() => {
    const prefix = resolvedCategory?.storage_box_prefix;
    if (!prefix) return null;
    const normalized = String(prefix).toUpperCase();
    const samePrefixBoxes = (storageBoxesList as ProviderStorageBoxRow[]).filter((b) =>
      String(b.name || '')
        .toUpperCase()
        .startsWith(`${normalized}-`)
    );
    const maxIndex = samePrefixBoxes.reduce((max, box) => {
      const m = String(box.name || '')
        .toUpperCase()
        .match(new RegExp(`^${normalized}-(\\d+)$`));
      const idx = m?.[1] ? Number.parseInt(m[1], 10) : 0;
      return Number.isFinite(idx) ? Math.max(max, idx) : max;
    }, 0);
    return `${normalized}-${maxIndex + 1}`;
  }, [resolvedCategory?.storage_box_prefix, storageBoxesList]);

  const autoGeneratedTitle = useMemo(
    () =>
      buildProviderFoundObjectTitle(
        resolvedCategory?.name || formData.objectType,
        formData.objectAttributes || {},
        resolvedCategory?.attributes ?? null
      ),
    [formData.objectAttributes, formData.objectType, resolvedCategory?.attributes, resolvedCategory?.name]
  );

  useEffect(() => {
    setFormData((prev) => {
      if (prev.title === autoGeneratedTitle) {
        return prev;
      }
      return {
        ...prev,
        title: autoGeneratedTitle,
      };
    });
  }, [autoGeneratedTitle]);

  const handleQuickImageSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setErrors((prev) => ({ ...prev, images: 'Veuillez sélectionner un fichier image.' }));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setErrors((prev) => ({ ...prev, images: "L'image ne doit pas dépasser 5MB." }));
      return;
    }
    if ((formData.images || []).length >= 5) {
      setErrors((prev) => ({ ...prev, images: 'Maximum 5 images.' }));
      return;
    }

    setImageUploading(true);
    try {
      const response = await apiService.uploadImage(file);
      const filename = response.data.filename;
      handleInputChange('images', [...(formData.images || []), filename]);
      setErrors((prev) => {
        const next = { ...prev };
        delete next.images;
        return next;
      });
    } catch {
      setErrors((prev) => ({ ...prev, images: "Erreur lors de l'upload de l'image." }));
    } finally {
      setImageUploading(false);
      if (event.target) event.target.value = '';
    }
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        handleDialogClose();
      }}
      disableEscapeKeyDown
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
      scroll="paper"
      TransitionProps={muiDialogTransitionProps}
    >
      <DialogTitle
        sx={{
          pr: 2,
          position: 'sticky',
          top: 0,
          zIndex: 10,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1.5} flexWrap="wrap">
          <Typography variant="h6">
            {autoGeneratedTitle}
          </Typography>
          {(formData.images || []).length > 0 && (
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                borderRadius: 2,
                px: 1,
                py: 0.75,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {(formData.images?.length || 0)}/5
              </Typography>
              <input
                ref={quickImageInputRef}
                type="file"
                accept="image/*"
                onChange={handleQuickImageSelect}
                style={{ display: 'none' }}
              />
              <Tooltip
                arrow
                placement="bottom-start"
                title={
                  <Box
                    component="img"
                    src={getAnnouncementImageUrl(formData.images?.[0])}
                    alt="Aperçu grand format"
                    sx={{
                      width: 280,
                      maxWidth: '70vw',
                      maxHeight: 280,
                      objectFit: 'contain',
                      display: 'block',
                      borderRadius: 1,
                    }}
                  />
                }
              >
                <Box
                  sx={{
                    cursor: 'zoom-in',
                    display: 'inline-flex',
                    mr: 0.5,
                  }}
                >
                  <Avatar
                    variant="rounded"
                    src={getAnnouncementImageUrl(formData.images?.[0])}
                    sx={{ width: 42, height: 42, borderRadius: 1.5 }}
                  />
                </Box>
              </Tooltip>
              <Tooltip title="Ajouter une photo">
                <span>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => quickImageInputRef.current?.click()}
                    disabled={imageUploading || (formData.images?.length || 0) >= 5}
                  >
                    {imageUploading ? <CircularProgress size={18} /> : <PhotoCameraIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Supprimer la photo principale">
                <span>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() =>
                      handleInputChange('images', (formData.images || []).filter((_, i) => i !== 0))
                    }
                    disabled={imageUploading}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          )}
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent dividers sx={{ px: { xs: 1.5, sm: 3 } }}>
          <Grid container spacing={2}>
            {errors.general && (
              <Grid item xs={12}>
                <Alert severity="error">{errors.general}</Alert>
              </Grid>
            )}

            {/* Images en premier */}
            {(formData.images || []).length === 0 && (
              <Grid item xs={12}>
                <ImageUpload
                  images={formData.images || []}
                  onImagesChange={(images) => handleInputChange('images', images)}
                  maxImages={5}
                  required
                  error={errors.images}
                  categoryDefaultImageUrl={resolvedCategory?.default_image_url}
                />
              </Grid>
            )}
            {(formData.images || []).length > 0 && errors.images && (
              <Grid item xs={12}>
                <Alert severity="error" sx={{ py: 0.5 }}>
                  {errors.images}
                </Alert>
              </Grid>
            )}

            {/* Quand ? */}
            <Grid item xs={12}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Quand ?
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                  <Button
                    type="button"
                    variant={
                      formData.foundAt === calendarDayStringDaysAgo(0) ? 'contained' : 'outlined'
                    }
                    onClick={() => handleInputChange('foundAt', calendarDayStringDaysAgo(0))}
                  >
                    Aujourd&apos;hui
                  </Button>
                  <Button
                    type="button"
                    variant={
                      formData.foundAt === calendarDayStringDaysAgo(1) ? 'contained' : 'outlined'
                    }
                    onClick={() => handleInputChange('foundAt', calendarDayStringDaysAgo(1))}
                  >
                    Hier
                  </Button>
                  <Button
                    type="button"
                    variant={
                      formData.foundAt === calendarDayStringDaysAgo(2) ? 'contained' : 'outlined'
                    }
                    onClick={() => handleInputChange('foundAt', calendarDayStringDaysAgo(2))}
                  >
                    Avant-hier
                  </Button>
                </Stack>
                <DatePicker
                  label="Choisir une date"
                  value={parseYmdToLocalDate(formData.foundAt)}
                  onChange={(date) => {
                    if (!date) {
                      handleInputChange('foundAt', '');
                      return;
                    }
                    handleInputChange('foundAt', toDateInputString(date));
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      error: !!errors.foundAt,
                      helperText: errors.foundAt,
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>

            {/* Catégorie */}
            <Grid item xs={12}>
              <FormControl fullWidth required error={!!errors.categoryId}>
                <InputLabel>Catégorie</InputLabel>
                <Select
                  value={formData.categoryId ?? ''}
                  onChange={(e) => handleCategoryChange(e.target.value as number)}
                  label="Catégorie"
                  disabled={categoriesLoading}
                  renderValue={(value) => {
                    if (value == null) {
                      return '';
                    }
                    const id = typeof value === 'number' ? value : Number(value);
                    if (Number.isNaN(id)) return '';
                    const cat = categoriesData?.find((c: Category) => c.id === id);
                    if (!cat) return '';
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CategoryIcon
                          icon={cat.icon}
                          customIconUrl={cat.custom_icon_url}
                          customIconFilename={cat.custom_icon_filename}
                          size="small"
                          color="primary.main"
                        />
                        {cat.name}
                      </Box>
                    );
                  }}
                >
                  {categoriesData?.map((category: Category) => (
                    <MenuItem key={category.id} value={category.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CategoryIcon
                          icon={category.icon}
                          customIconUrl={category.custom_icon_url}
                          customIconFilename={category.custom_icon_filename}
                          size="small"
                          color="primary.main"
                        />
                        {category.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {providerInfo?.provider_type?.annual_event && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Événement / édition</InputLabel>
                  <Select
                    value={formData.provider_event_id ?? ''}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        provider_event_id: e.target.value === '' ? null : Number(e.target.value),
                      }))
                    }
                    label="Événement / édition"
                  >
                    <MenuItem value="">
                      <em>Aucun</em>
                    </MenuItem>
                    {providerEvents.map((ev) => (
                      <MenuItem key={ev.id} value={ev.id}>
                        {ev.name} ({ev.year})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {object && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Statut</InputLabel>
                  <Select
                    label="Statut"
                    value={formData.status}
                    onChange={(e) =>
                      handleInputChange('status', e.target.value as FoundObjectStatus)
                    }
                  >
                    <MenuItem value="found">Trouvé</MenuItem>
                    <MenuItem value="to_ship">À expédier</MenuItem>
                    <MenuItem value="in_transit">Parti en livraison</MenuItem>
                    <MenuItem value="claimed">Récupéré</MenuItem>
                    <MenuItem value="disposed">Éliminé</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* Champs de lieu / stockage : template + boîtes partenaire (provider-items) */}
            {providerTemplate || providerItemsList.length > 0 || storageBoxesList.length > 0 ? (
              <LocationFields
                template={providerTemplate || null}
                locationData={formData.locationData || {}}
                onLocationDataChange={handleLocationDataChange}
                providerConfig={providerInfo?.config}
                providerItems={providerItemsList}
                storageBoxes={storageBoxesList}
                hideStorageBox={!object}
                placeStorageMode={isSplitMode ? 'split' : 'combined'}
              />
            ) : (
              /* Fallback: champ de lieu statique si pas de template */
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Lieu de trouvaille"
                  value={formData.location}
                  onChange={(e) => handleInputChange('location', e.target.value)}
                  required
                  error={!!errors.location}
                  helperText={errors.location}
                />
              </Grid>
            )}
            {/* Attributs dynamiques de la catégorie */}
            {resolvedCategory && convertAttributesToArray(resolvedCategory.attributes).map((attribute) => (
              <Grid item xs={12} sm={6} key={attribute.name}>
                {renderAttributeField(attribute)}
              </Grid>
            ))}

            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                multiline
                minRows={3}
                maxRows={8}
                label="Détail secret (non public)"
                placeholder={secretCluesHint}
                value={
                  typeof formData.objectAttributes?.[SECRET_OBJECT_ATTRIBUTE_KEY] === 'string'
                    ? formData.objectAttributes[SECRET_OBJECT_ATTRIBUTE_KEY]
                    : ''
                }
                onChange={(e) => handleAttributeChange(SECRET_OBJECT_ATTRIBUTE_KEY, e.target.value)}
                error={Boolean(errors[SECRET_OBJECT_ATTRIBUTE_KEY])}
                helperText={
                  errors[SECRET_OBJECT_ATTRIBUTE_KEY]
                    ?? (resolvedCategory
                      ? resolvedCategory.secret_clues_placeholder?.trim()
                        ? secretCluesHint
                        : `${secretCluesHint} Ce détail aide à authentifier le propriétaire lors d'une restitution.`
                      : 'Choisissez une catégorie pour afficher l\'aide de saisie du détail secret.')
                }
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  endAdornment: (
                    <VisibilityOffIcon sx={{ color: 'text.disabled', fontSize: 22, mr: 0.5 }} />
                  ),
                }}
              />
            </Grid>

            {/* Erreurs générales */}
            {Object.keys(errors).length > 0 && (
              <Grid item xs={12}>
                <Alert severity="error">
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {Object.entries(errors).map(([key, message]) => (
                      <li key={key}>{message}</li>
                    ))}
                  </ul>
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleDialogClose} disabled={isLoading}>
            Annuler
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading || !formData.categoryId}
            startIcon={isLoading ? <CircularProgress size={20} /> : null}
          >
            {object ? 'Modifier' : 'Créer'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>

      <Dialog
        open={storagePickOpen}
        onClose={() => {
          if (!createMutation.isPending) {
            releaseFocus();
            setStoragePickOpen(false);
          }
        }}
        maxWidth="sm"
        fullWidth
        fullScreen={fullScreen}
        TransitionProps={muiDialogTransitionProps}
      >
        <DialogTitle>Enregistrer — choix de la boîte</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Alert severity="info">
            {resolvedCategory
              ? (
                <>
                  Boîtes proposées pour la catégorie <strong>{resolvedCategory.name}</strong>. Après validation,
                  vous obtiendrez une référence à inscrire sur l&apos;étiquette.
                </>
              )
              : 'Choisissez d’abord une catégorie pour proposer la boîte adaptée.'}
          </Alert>
          {resolvedCategory && availableCategoryBoxes.length === 0 && (
            <Alert severity="warning">
              Aucune boîte disponible pour cette catégorie.
              {nextCategoryBoxLabel ? ` Vous pouvez créer ${nextCategoryBoxLabel}.` : ''}
            </Alert>
          )}
          {fullCategoryBoxes.length > 0 && (
            <Alert severity="info">
              {fullCategoryBoxes.length} boîte(s) marquée(s) pleine(s) ignorée(s) pour cette catégorie.
            </Alert>
          )}
          <FormControl fullWidth required error={!!errors.storage}>
            <InputLabel>Boîte de stockage</InputLabel>
            <Select
              value={storagePickSelection}
              label="Boîte de stockage"
              onChange={(e) => setStoragePickSelection(e.target.value)}
              disabled={createMutation.isPending}
            >
              <MenuItem value="">
                <em>—</em>
              </MenuItem>
              {isSplitMode
                ? availableCategoryBoxes
                    .sort((a, b) => getStorageBoxOrderScore(b) - getStorageBoxOrderScore(a))
                    .map((box) => (
                      <MenuItem key={box.id} value={String(box.id)}>
                        {box.name}
                      </MenuItem>
                    ))
                : Object.keys(groupedStorageByPlace)
                .sort((a, b) => a.localeCompare(b, 'fr'))
                .flatMap((placeLabel) => {
                  const matching = groupedStorageByPlace[placeLabel].filter((box) =>
                    availableCategoryBoxes.some((cb) => cb.id === box.id)
                  );
                  if (matching.length === 0) return [];
                  matching.sort((a, b) => getStorageBoxOrderScore(b) - getStorageBoxOrderScore(a));
                  return [
                    <ListSubheader key={`sp-${placeLabel}`}>{placeLabel}</ListSubheader>,
                    ...matching.map((box) => (
                      <MenuItem key={box.id} value={String(box.id)}>
                        {box.name}
                      </MenuItem>
                    )),
                  ];
                })}
            </Select>
            {errors.storage && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                {errors.storage}
              </Typography>
            )}
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              releaseFocus();
              setStoragePickOpen(false);
            }}
            disabled={createMutation.isPending}
          >
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmStorageAndCreate}
            disabled={createMutation.isPending || !storagePickSelection}
            startIcon={createMutation.isPending ? <CircularProgress size={18} /> : null}
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={postRegisterOpen && !!postRegisterData}
        onClose={handlePostRegisterDone}
        maxWidth="sm"
        fullWidth
        fullScreen={fullScreen}
        TransitionProps={muiDialogTransitionProps}
      >
        <DialogTitle>Objet enregistré</DialogTitle>
        <DialogContent sx={{ mt: 1 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Collez une étiquette sur l&apos;objet avec le numéro :{' '}
            {postRegisterData?.reference_code ? (
              <FormattedReferenceCode
                code={postRegisterData.reference_code}
                component="span"
                suffixOnly
              />
            ) : null}
          </Typography>
          <Alert severity="success">
            « {postRegisterData?.title} » (
            {postRegisterData?.reference_code ? (
              <FormattedReferenceCode
                code={postRegisterData.reference_code}
                component="span"
                suffixOnly
              />
            ) : (
              '—'
            )}
            ) a bien été enregistré.
            {postRegisterData?.boxName && postRegisterData.boxName !== '—' ? (
              <>
                {' '}
                Stockez-le dans la boîte : <strong>{postRegisterData.boxName}</strong>.
              </>
            ) : (
              <> Conservez-le conformément au lieu renseigné.</>
            )}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={handlePostRegisterDone}>
            C&apos;est fait
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FoundObjectDialog;